import { TimePicker as AntdTimePicker, message } from "antd"
import { COLORS } from "components/layout-components/timenotes-theme"
import moment, { Moment } from "moment-timezone"
import React, { FocusEventHandler, useEffect, useState } from "react"
import styled from "styled-components"

interface TimePickerProps extends React.ComponentProps<typeof AntdTimePicker> {
  inPlaceEditor?: boolean
  triggerChangeOnSelect?: boolean
}

const AntdTimePickerStyled = styled(AntdTimePicker)`
  &.ant-picker.ant-picker-borderless:hover {
    background-color: ${COLORS.editableHighlightGray} !important;
  }
`

const TimePicker = (props: TimePickerProps) => {
  const { inPlaceEditor } = props

  // default borderd to be true, except if inPlaceEditor mode
  const defaultBordered = props.bordered === undefined ? (inPlaceEditor ? false : undefined ): props.bordered
  const [bordered, setBordered] = useState(defaultBordered)
  const [isValid, setIsValid] = useState(true)

  // default input value based on 
  const defaultInputValue = props.value ? props.value.format(props.format as any) : ""
  /*
  if (props.value) {
    if (props.format && typeof(props.format) === "string") {
      defaultInputValue = props.value.format(props.format)
    } else {
      defaultInputValue = props.value.format()
    }
  }
  */
  const [inputValue, setInputValue] = useState(defaultInputValue)

  const triggerChangeOnSelect = (value: Moment) => {
    if (moment.isMoment(value) && props.onChange) {
      props.onChange(value as moment.Moment, value.format() as string)
    }
  }

  let keyUpBinded = false
  const handleKeyUp = (e: any) => {
    const input = e.target as HTMLInputElement
    setInputValue(input.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!keyUpBinded) {
      e.target.addEventListener('keyup', (event) => { handleKeyUp(event) })
      keyUpBinded = true
    }
  }

  const handleOnFocus = () => {
    if (inPlaceEditor && !props.bordered) {
      setBordered(true)
    }
  }

  const handleOnBlur = (e: any) => {
    if (inPlaceEditor && !props.bordered) {
      setBordered(false)
    }

    if (isValid) {
      const inputMoment = moment.utc(inputValue, props.format as any, true)
      // format does not matter as long as they both match
      if (props.value?.format("HH:mm") !== inputMoment.format("HH:mm")) {
        // if input value changed from the initial value on blur
        // and is a valid one - trigger onChange event!

        //TODO: if value is provided by props and onChange is happening
        // we would like to keep the input value for the time of change process
        if (props.value && props.onChange) {
          props.onChange && props.onChange(inputMoment, inputMoment.format(props.format as any))
        }
      }
    }

    // on blur input value always goes to valid state anyway
    setIsValid(true)
  }

  const handleOnSelect = (value: moment.Moment) => {
    setInputValue(value.format(props.format as any))
  }

  useEffect(() => {
    const inputMoment = moment(inputValue, props.format as any, true)
    setIsValid(inputMoment.isValid())
  }, [inputValue])

  const style = props.style || {}
  if (!isValid) {
    style['border'] = '1px solid red'
  }

  const onChange = (value: moment.Moment | null, dateString: string) => {
    if (props.onChange) {
      props.onChange(value, dateString)
    }

    if (inPlaceEditor && !props.bordered) {
      setBordered(false)
    }
  }

  return (
    <AntdTimePickerStyled
      {...props}
      style={style}
      bordered={bordered}
      onSelect={handleOnSelect}
      onKeyDown={handleKeyDown}
      onFocus={handleOnFocus}
      onChange={onChange}
      onBlur={handleOnBlur}
    />
  )
}

export default TimePicker